import { forwardRef } from 'react'
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, Ref } from 'react'

type SiteButtonVariant = 'solid' | 'ghost'

type SiteButtonOwnProps = {
    variant?: SiteButtonVariant;
}

type SiteButtonProps =
    | ({ href?: undefined } & SiteButtonOwnProps & ButtonHTMLAttributes<HTMLButtonElement>)
    | ({ href: string } & SiteButtonOwnProps & AnchorHTMLAttributes<HTMLAnchorElement>)

const VARIANT_CLASSES: Record<SiteButtonVariant, string> = {
    solid: 'border-2 border-unimo-site-cta bg-unimo-site-cta text-white',
    // For lower-emphasis CTAs that don't purchase directly, e.g. "Talk to
    // sales" next to a solid "Buy unimo" — same shape, not the primary action.
    ghost: 'border-2 border-unimo-site-cta bg-transparent text-unimo-site-cta',
}

// Renders as a real <a> when given an href (site CTAs that navigate), or a
// plain <button> otherwise (CTAs with no destination yet, e.g. "Buy unimo").
export const SiteButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, SiteButtonProps>(
    ({ className = '', children, href, variant = 'solid', ...props }, ref) => {
        const classes = `inline-flex items-center justify-center rounded-site-card px-6 py-3 font-medium ${VARIANT_CLASSES[variant]} ${className}`

        if (href) {
            return (
                <a
                    ref={ref as Ref<HTMLAnchorElement>}
                    href={href}
                    className={classes}
                    {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
                >
                    {children}
                </a>
            )
        }

        return (
            <button
                ref={ref as Ref<HTMLButtonElement>}
                className={classes}
                {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
            >
                {children}
            </button>
        )
    }
)

SiteButton.displayName = 'SiteButton'

export default SiteButton
